/**
 * BanksPage - Página de gestión de bancos (CRUD)
 * Solo accesible por administradores
 */
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, FilterList } from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  ConfirmDialog,
  DataTable,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as catalogoService from '../services/catalogoService';
import { ROLES } from '../utils';

// Esquema de validación
const bancoSchema = yup.object().shape({
  empresa_id: yup.string().required('Debe seleccionar una empresa'),
  nombre: yup.string().required('El nombre es requerido'),
  codigo: yup
    .string()
    .required('El código es requerido')
    .matches(/^[0-9]+$/, 'El código debe contener solo números'),
  activo: yup.boolean(),
});

const BanksPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bancos, setBancos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanco, setEditingBanco] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(bancoSchema),
    defaultValues: {
      empresa_id: '',
      nombre: '',
      codigo: '',
      activo: true,
    },
  });

  // Verificar que el usuario sea administrador
  useEffect(() => {
    if (user && user.rol !== ROLES.ADMINISTRADOR) {
      showError('No tiene permisos para acceder a esta página');
      window.history.back();
    }
  }, [user]);

  // Cargar bancos y empresas
  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatus !== 'all') {
        filters.activo = filterStatus === 'active';
      }
      const [bancosData, empresasData] = await Promise.all([
        catalogoService.getBancos(filters),
        catalogoService.getEmpresas({ activo: true }),
      ]);
      setBancos(bancosData);
      setEmpresas(empresasData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  // Abrir diálogo para crear
  const handleOpenCreate = () => {
    setEditingBanco(null);
    reset({
      empresa_id: '',
      nombre: '',
      codigo: '',
      activo: true,
    });
    setOpenDialog(true);
  };

  // Abrir diálogo para editar
  const handleOpenEdit = (banco) => {
    setEditingBanco(banco);
    reset({
      empresa_id: banco.empresa?.id || banco.empresa_id,
      nombre: banco.nombre,
      codigo: banco.codigo,
      activo: banco.activo !== false,
    });
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBanco(null);
    reset();
  };

  // Guardar banco (crear o actualizar)
  const onSubmit = async (data) => {
    try {
      if (editingBanco) {
        await catalogoService.updateBanco(editingBanco.id, data);
        showSuccess('Banco actualizado correctamente');
      } else {
        await catalogoService.createBanco(data);
        showSuccess('Banco creado correctamente');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error al guardar banco:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al guardar banco';
      showError(errorMessage);
    }
  };

  // Confirmar eliminación
  const handleDelete = (banco) => {
    setDeleteConfirm(banco);
  };

  // Eliminar banco
  const confirmDelete = async () => {
    try {
      await catalogoService.deleteBanco(deleteConfirm.id);
      showSuccess('Banco eliminado correctamente');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error al eliminar banco:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Error al eliminar banco';
      showError(errorMessage);
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      field: 'empresa',
      headerName: 'Empresa',
      renderCell: (row) => row.empresa?.nombre || '-',
    },
    {
      field: 'nombre',
      headerName: 'Nombre',
    },
    {
      field: 'codigo',
      headerName: 'Código',
    },
    {
      field: 'activo',
      headerName: 'Estado',
      type: 'status',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando bancos..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Bancos"
        breadcrumbs={[{ label: 'Bancos' }]}
        actions={[
          {
            label: 'Nuevo Banco',
            icon: AddIcon,
            onClick: handleOpenCreate,
            variant: 'contained',
          },
        ]}
      />

      {/* Filtros */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FilterList />
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setFilterStatus(newValue);
            }
          }}
          size="small"
        >
          <ToggleButton value="all">Todos</ToggleButton>
          <ToggleButton value="active">Activos</ToggleButton>
          <ToggleButton value="inactive">Inactivos</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tabla de bancos */}
      <DataTable
        columns={columns}
        data={bancos}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar por nombre o código..."
        emptyMessage="No hay bancos registrados"
      />

      {/* Diálogo de crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingBanco ? 'Editar Banco' : 'Nuevo Banco'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Controller
                name="empresa_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Empresa"
                    fullWidth
                    error={!!errors.empresa_id}
                    helperText={errors.empresa_id?.message}
                    autoFocus
                  >
                    <MenuItem value="">Seleccione una empresa</MenuItem>
                    {empresas.map((empresa) => (
                      <MenuItem key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <TextField
                {...register('nombre')}
                label="Nombre"
                fullWidth
                error={!!errors.nombre}
                helperText={errors.nombre?.message}
              />

              <TextField
                {...register('codigo')}
                label="Código"
                fullWidth
                error={!!errors.codigo}
                helperText={errors.codigo?.message}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    {...register('activo')}
                    defaultChecked={editingBanco?.activo !== false}
                  />
                }
                label="Activo"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingBanco ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Eliminar Banco"
        message={`¿Está seguro de que desea eliminar el banco "${deleteConfirm?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />
    </Box>
  );
};

export default BanksPage;
